import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";

import { wall } from "../upfrontSlotsHelpers";

// Picks and the weeks the repeat had to step over read as one series, so they
// are interleaved by date rather than shown as two lists.
const buildRows = (picks, blockedWeeks) =>
  [
    ...picks.map((p) => ({ kind: "pick", sortKey: p.date, pick: p })),
    ...blockedWeeks.map((b) => ({ kind: "blocked", sortKey: b.date, week: b }))
  ].sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));

function PicksList({ picks, blockedWeeks = [], showLocation, onRemove, onOpenBlocked, onDismissBlocked }) {
  const rows = buildRows(picks, blockedWeeks);
  let pickNo = 0;

  return (
    <Stack spacing={1}>
      {rows.map((row) => {
        // Never a real slot — the server never offered this time, so the row is
        // a placeholder in the series that sends the client to that day to take
        // another one.
        if (row.kind === "blocked") {
          const { week } = row;
          return (
            <Card
              key={`blocked-${week.date}`}
              variant="outlined"
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderColor: "error.main",
                bgcolor: (t) => alpha(t.palette.error.main, 0.06)
              }}
            >
              <Box
                sx={{
                  width: "2rem",
                  height: "2rem",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                  color: "error.main",
                  flexShrink: 0
                }}
              >
                <EventBusyIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="error.main">
                  {wall(week.date).toFormat("ccc d LLL")} · {week.time} · unavailable
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {week.trainerName ? `${week.trainerName} is booked` : "Already taken"} — pick another time that day
                </Typography>
              </Box>
              <Button size="small" color="error" onClick={() => onOpenBlocked(week)} sx={{ textTransform: "none", flexShrink: 0 }}>
                Choose
              </Button>
              <IconButton size="small" onClick={() => onDismissBlocked(week.date)} aria-label="Dismiss unavailable week">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Card>
          );
        }

        const p = row.pick;
        pickNo += 1;
        return (
          <Card key={p.key} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: "2rem",
                height: "2rem",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                fontWeight: 700,
                fontSize: "0.85rem",
                flexShrink: 0
              }}
            >
              {pickNo}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {wall(p.startDate).toFormat("ccc d LLL")} · {p.time}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {[p.trainerName, p.facilityName, showLocation ? p.locationName : null].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => onRemove(p.key)} aria-label="Remove session">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Card>
        );
      })}
    </Stack>
  );
}

export default PicksList;
